import { DocumentUploaderItem } from '@app/shared/types/sf-data'

import { Ref, ref } from 'vue'
import useSfPolling from '@app/shared/vue/composables/use-sf-polling'
import { UIState } from '@app/application/ai_ocr_requests/types'
import { SfLog } from '@app/adapters/logger'
import useI18n from '@app/shared/vue/composables/use-I18n'

import { useOCRUI } from '../store/use-ocr-ui'

const { startPolling } = useSfPolling()

export const useDocumentUpload = (): {
  triggerDocumentUpload: () => Promise<void>
  userManagedDocuments: Ref<DocumentUploaderItem[]>
  removeSubscription: () => void
  subscribeToUploadProgress: (endpoint: string, lastId: number) => void
  handleError: (message: string, e?: unknown) => void
} => {
  const userManagedDocuments = ref<DocumentUploaderItem[]>([])
  const I18n = useI18n()
  let unsubscribe: null | (() => void)
  const uiStore = useOCRUI()

  function triggerDocumentUpload(): Promise<void> {
    return new Promise((resolve) => {
      window.modalCitizen.openDocumentSelectModal({
        firmId: window.Silverfin.currentFirmId,
        companyId: window.Silverfin.currentCompanyId,
        options: {
          fileCollectionValue: [],
          modalOpened: (): void => {
            // Change the Save button name
            // until we have a new modal to upload documents
            // https://gitlab.silverfin.com/development/silverfin/-/issues/22589
            const saveButton = document.querySelector(
              '[data-behaviour="document-select-file-collection-save"]'
            )

            if (saveButton) {
              saveButton.textContent = I18n.t(
                'ai_ocr_requests.analyse_files_button'
              )
            }
            resolve()
          },
          type: 'file_collection',
          success: (documents, _deletedDocumentIds, successCallback): void => {
            userManagedDocuments.value = documents

            successCallback()
          },
        },
      })
    })
  }

  function subscribeToUploadProgress(endpoint: string, lastId: number): void {
    removeSubscription()

    unsubscribe = startPolling(endpoint, lastId, () => true, uploadCompleted)
  }

  function removeSubscription(): void {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  function uploadCompleted(
    response:
      | string
      | {
          error_messages: [{ filename: string; key: string; params: undefined }]
        }
  ): void {
    if (response === 'complete') {
      removeSubscription()
      uiStore.transitionTo(UIState.Success)
      window.Turbolinks.visit(window.location.href)
    } else {
      if (typeof response === 'object') {
        const errorMessages = response.error_messages.map(
          (err) => `${err.filename}: ${I18n.t(err.key, err.params)}`
        )

        handleError(errorMessages.join('\n'))
        return
      }

      handleError('Error uploading document')
    }
  }

  function handleError(message: string, e?: unknown): void {
    uiStore.transitionTo(UIState.Error)

    SfLog.info(`AI-OCR-DOCUMENTS::${message}`, {
      error: e,
      extra: {
        companyId: window.Silverfin.currentCompanyId,
        firmId: window.Silverfin.currentFirmId,
        error: e,
      },
    })
  }

  return {
    triggerDocumentUpload,
    userManagedDocuments,
    removeSubscription,
    subscribeToUploadProgress,
    handleError,
  }
}
